import React, { useMemo } from 'react';
import type { Binding, CanvasComponent, RestAction, Variable } from '../../types/project';
import { responseTargetOptions } from './configurationUtils';
import styles from './ActionInspector.module.css';

type Props = {
  binding: Binding;
  actions: RestAction[];
  components: CanvasComponent[];
  variables: Record<string, Variable>;
  onChange: (binding: Binding) => void;
  onDone: () => void;
};

export default function ResponseBindingEditor({ binding, actions, components, variables, onChange, onDone }: Props): React.ReactElement {
  const targets = useMemo(() => responseTargetOptions(components, variables), [components, variables]);
  const isLegacyOnClick = binding.trigger === 'onClick';
  const sourceMatch = /^actions\.([^.]+)\.response(?:\.(.*))?$/.exec(binding.source);
  const actionId = sourceMatch?.[1] ?? '';
  const responsePath = sourceMatch?.[2] ?? '';
  const updateSource = (nextActionId: string, nextPath: string) => onChange({
    ...binding,
    source: `actions.${nextActionId}.response${nextPath.trim() ? `.${nextPath.trim().replace(/^\.+/, '')}` : ''}`,
  });

  return (
    <div className={styles.configurationEditor} data-testid={`binding-editor-${binding.id}`}>
      <label className={styles.formField}>
        <span className={styles.formLabel}>REST action response</span>
        <select className={styles.formInput} aria-label="Binding response action" value={actionId} onChange={(event) => updateSource(event.target.value, responsePath)}>
          {actions.map((action) => <option key={action.id} value={action.id}>{action.name}</option>)}
        </select>
      </label>
      <label className={styles.formField}>
        <span className={styles.formLabel}>Response path</span>
        <input className={styles.formInput} aria-label="Binding response path" value={responsePath} placeholder="body or body.items" onChange={(event) => updateSource(actionId, event.target.value)} />
        <span className={styles.formHint}>Leave blank for the full response envelope.</span>
      </label>
      <label className={styles.formField}>
        <span className={styles.formLabel}>Target</span>
        <select className={styles.formInput} aria-label="Binding target" value={binding.target} onChange={(event) => onChange({ ...binding, target: event.target.value })}>
          {!targets.some((target) => target.value === binding.target) && <option value={binding.target}>{binding.target} (unsupported or missing)</option>}
          {targets.map((target) => <option key={target.value} value={target.value}>{target.label}</option>)}
        </select>
      </label>
      <label className={styles.formField}>
        <span className={styles.formLabel}>Trigger</span>
        <select className={styles.formInput} aria-label="Binding trigger" value={binding.trigger ?? 'onChange'} onChange={(event) => onChange({ ...binding, trigger: event.target.value })}>
          <option value="onSuccess">On success</option>
          {isLegacyOnClick && <option value="onClick">On click (legacy compatibility)</option>}
          {(binding.trigger !== 'onSuccess' && !isLegacyOnClick) && <option value={binding.trigger ?? 'onChange'}>{binding.trigger ?? 'onChange'} (existing)</option>}
        </select>
        {isLegacyOnClick && <span className={styles.formHint}>This existing legacy trigger remains compatible. Choose On success to migrate it; new bindings cannot select On click.</span>}
      </label>
      {binding.transform !== undefined && (
        <div className={styles.formHint} role="note">
          This existing transform is retained for compatibility but is not executed. Remove it in the JSON Editor to clear the warning.
        </div>
      )}
      <button type="button" className={styles.primaryButton} onClick={onDone}>Done</button>
    </div>
  );
}
